import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  collectionGroup,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { getFirestoreInstance } from './firestore';
import type { Quote } from '@gigdriver/domain';
import { fromFirestore } from './serde';
import { getGig, updateGig } from './gigs';

function quotesCollection(orgId: string, gigId: string) {
  return collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'quotes');
}

// Creates a quote and points the gig's activeQuoteId at it.
export async function createQuote(
  orgId: string,
  gigId: string,
  quote: Omit<Quote, 'id' | 'orgId' | 'gigId' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const ref = doc(quotesCollection(orgId, gigId));
  await setDoc(ref, {
    ...quote,
    id: ref.id,
    orgId,
    gigId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await updateGig(orgId, gigId, { activeQuoteId: ref.id });
  return ref.id;
}

export async function getQuote(orgId: string, gigId: string, quoteId: string): Promise<Quote | null> {
  const ref = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'quotes', quoteId);
  const snap = await getDoc(ref);
  return snap.exists() ? fromFirestore<Quote>(snap.data()) : null;
}

export async function getGigQuotes(orgId: string, gigId: string): Promise<Quote[]> {
  const snap = await getDocs(quotesCollection(orgId, gigId));
  return snap.docs.map(d => fromFirestore<Quote>(d.data()));
}

// The gig's current quote, if any — resolved via Gig.activeQuoteId.
export async function getActiveQuote(orgId: string, gigId: string): Promise<Quote | null> {
  const gig = await getGig(orgId, gigId);
  if (!gig?.activeQuoteId) return null;
  return getQuote(orgId, gigId, gig.activeQuoteId);
}

export async function updateQuote(
  orgId: string,
  gigId: string,
  quoteId: string,
  updates: Partial<Quote>,
): Promise<void> {
  const ref = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'quotes', quoteId);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
}

// Clones an approved quote into a new DRAFT quote linked back to it
// (parentQuoteId), with every carried-over line marked `locked` (a display
// hint only — still editable/removable like any other line). Points the
// gig's activeQuoteId at the new quote (via createQuote).
export async function createChangeOrder(
  orgId: string,
  gigId: string,
  approvedQuoteId: string,
): Promise<string> {
  const parent = await getQuote(orgId, gigId, approvedQuoteId);
  if (!parent) throw new Error('Quote to base the change order on was not found');

  return createQuote(orgId, gigId, {
    clientId: parent.clientId,
    state: 'DRAFT',
    currency: parent.currency,
    lineItems: parent.lineItems.map(line => ({ ...line, locked: true })),
    taxRate: parent.taxRate,
    depositAmount: parent.depositAmount,
    subtotal: parent.subtotal,
    tax: parent.tax,
    total: parent.total,
    parentQuoteId: parent.id,
    changeOrderNumber: (parent.changeOrderNumber ?? 0) + 1,
  });
}

// Every quote across the org's gigs, live (manager+; Gigs list "Budget"
// column). Gig.activeQuoteId picks the current one per gig — a Gig no longer
// carries a typed-in quoted amount; the figure is the quote's own total.
export function watchOrgQuotes(
  orgId: string,
  onData: (quotes: Quote[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collectionGroup(getFirestoreInstance(), 'quotes'), where('orgId', '==', orgId));
  return onSnapshot(q, snap => onData(snap.docs.map(d => fromFirestore<Quote>(d.data()))), err => onError?.(err));
}
